import type { ClientRecordTabId } from '@/lib/clients/resolve-initial-client-tab';
import { CLIENT_RECORD_MAC_ID_ANCHOR } from '@/lib/clients/client-record-zone-d-notices';

export const CLIENT_RECORD_DETAILS_ANCHOR = 'client-details';

export type ClientDetailsTabContext = {
  sahHubVisible: boolean;
  racHubVisible: boolean;
};

/** Phase 4: Journey/Residential cohorts use global chrome; hide the hollow overview tab. */
export function clientDetailsTabHidden(_ctx: ClientDetailsTabContext): boolean {
  // Wave 1 Slice B (ratified 2026-07-07): hub clients keep their Overview tab.
  // The previous policy hid it and folded client details INTO the hub page,
  // which made ?tab=overview and the Journey render byte-identical and turned
  // the Journey into a six-form dump (live-capture evidence in
  // reports/wave1-element-audit-2026-07-07.md). Overview = identity/details;
  // Journey/Residential = lifecycle. The fold-in render sites key off this.
  return false;
}

/** care_today_v1 OFF keeps the established Home label. */
export function clientDetailsV2GroupLabel(careToday = false): string {
  return careToday ? 'Care Today' : 'Home';
}

export function remapHiddenClientDetailsTab(
  tab: ClientRecordTabId,
  ctx: ClientDetailsTabContext,
): ClientRecordTabId {
  if (tab !== 'overview' || !clientDetailsTabHidden(ctx)) return tab;
  if (ctx.sahHubVisible) return 'sah-hub';
  // RAC overview depth lives on People (#client-details), not Residential hub.
  if (ctx.racHubVisible) return 'family';
  return tab;
}

export function clientDetailsTabHref(
  clientId: string,
  ctx: ClientDetailsTabContext,
  hash?: string,
): string {
  const base = `/clients/${encodeURIComponent(clientId)}`;
  // panel= mirrors the hash so the deep link survives the in-place tab URL
  // sync (which can race and drop hashes — 2026-07-07 blocker-CTA audit).
  const suffix = hash ? `&panel=${hash}#${hash}` : '';
  if (clientDetailsTabHidden(ctx)) {
    const tab = ctx.sahHubVisible ? 'sah-hub' : 'family';
    return `${base}?tab=${tab}${suffix}`;
  }
  return `${base}?tab=overview${suffix}`;
}

export function clientRecordMacIdHref(clientId: string, ctx: ClientDetailsTabContext): string {
  if (clientDetailsTabHidden(ctx)) {
    return `/clients/${encodeURIComponent(clientId)}#${CLIENT_RECORD_MAC_ID_ANCHOR}`;
  }
  return `/clients/${encodeURIComponent(clientId)}?tab=overview&panel=${CLIENT_RECORD_MAC_ID_ANCHOR}#${CLIENT_RECORD_MAC_ID_ANCHOR}`;
}

export function clientDetailsPrimaryHref(clientId: string, ctx: ClientDetailsTabContext): string {
  if (clientDetailsTabHidden(ctx)) {
    if (ctx.sahHubVisible) {
      return `/clients/${encodeURIComponent(clientId)}?tab=sah-hub&panel=${CLIENT_RECORD_DETAILS_ANCHOR}#${CLIENT_RECORD_DETAILS_ANCHOR}`;
    }
    if (ctx.racHubVisible) {
      return `/clients/${encodeURIComponent(clientId)}?tab=family&panel=${CLIENT_RECORD_DETAILS_ANCHOR}#${CLIENT_RECORD_DETAILS_ANCHOR}`;
    }
  }
  return clientDetailsTabHref(clientId, ctx, CLIENT_RECORD_DETAILS_ANCHOR);
}

export function clientDetailsReviewHref(clientId: string, ctx: ClientDetailsTabContext): string {
  if (clientDetailsTabHidden(ctx)) {
    return clientDetailsPrimaryHref(clientId, ctx);
  }
  return clientDetailsTabHref(clientId, ctx, 'client-record-details-communication');
}

export function clientDetailsSignatoryHref(clientId: string, ctx: ClientDetailsTabContext): string {
  if (clientDetailsTabHidden(ctx)) {
    return `/clients/${encodeURIComponent(clientId)}?tab=family&panel=agreement-signatory#agreement-signatory`;
  }
  return clientDetailsTabHref(clientId, ctx, 'agreement-signatory');
}

export function remapHiddenProfileTabHref(href: string, ctx: ClientDetailsTabContext): string {
  if (!clientDetailsTabHidden(ctx)) return href;
  return href.replace(/\?tab=overview\b/g, ctx.sahHubVisible ? '?tab=sah-hub' : '?tab=family');
}

export type RemappedOverviewDeepLink = {
  tab: 'sah-hub' | 'rac-hub' | 'family' | 'overview';
  hash?: string;
};

/** Rewrites legacy ?tab=overview deep links when the details tab is hidden. */
export function remapHiddenOverviewDeepLink(
  hash: string | undefined,
  ctx: ClientDetailsTabContext,
): RemappedOverviewDeepLink {
  if (!clientDetailsTabHidden(ctx)) {
    return { tab: 'overview', hash: hash || undefined };
  }
  if (hash === 'agreement-signatory') {
    return { tab: 'family', hash: 'agreement-signatory' };
  }
  if (
    hash === 'client-record-details' ||
    hash === 'client-record-details-communication' ||
    hash === CLIENT_RECORD_DETAILS_ANCHOR
  ) {
    if (ctx.sahHubVisible) {
      return { tab: 'sah-hub', hash: CLIENT_RECORD_DETAILS_ANCHOR };
    }
    if (ctx.racHubVisible) {
      return { tab: 'family', hash: CLIENT_RECORD_DETAILS_ANCHOR };
    }
  }
  if (ctx.racHubVisible) {
    return { tab: 'family', hash: CLIENT_RECORD_DETAILS_ANCHOR };
  }
  return { tab: 'sah-hub', hash: hash || undefined };
}
